import React from "react";

import { AlertTriangle, CreditCard } from "lucide-react";

import SalesConditionSelector from "~/components/SalesConditionSelector";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { ClientRiskData } from "~/pages/risk/components/ClientRiskTable";
import { formatDate } from "~/utils/helpers";

interface ClientRiskEditorModalProps {
  client: ClientRiskData;
  insuredAmount: number;
  trailingPaymentDays: number;
  salesConditionId: string | undefined;
  setInsuredAmount: (value: number) => void;
  setTrailingPaymentDays: (value: number) => void;
  setSalesConditionId: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

const ClientRiskEditorModal: React.FC<ClientRiskEditorModalProps> = ({
  client,
  insuredAmount,
  trailingPaymentDays,
  salesConditionId,
  setInsuredAmount,
  setTrailingPaymentDays,
  setSalesConditionId,
  onSave,
  onCancel,
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="mx-auto w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertTriangle className="mr-2 h-5 w-5 text-amber-500" />
            Evaluación de Cliente
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="text-lg font-medium">{client.name}</h3>
            <p className="text-sm text-muted-foreground">{client.taxId}</p>
            <p className="text-sm text-muted-foreground">
              Creado: {formatDate(client.createdAt)}
            </p>
          </div>

          <Separator />

          <Tabs defaultValue="riskAssessment" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="riskAssessment">
                Evaluación de Riesgo
              </TabsTrigger>
              <TabsTrigger value="salesCondition">
                Condición de Venta
              </TabsTrigger>
            </TabsList>

            <TabsContent value="riskAssessment" className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="insuredAmount">Monto Asegurado ($)</Label>
                <Input
                  id="insuredAmount"
                  type="number"
                  value={insuredAmount}
                  onChange={(e) => setInsuredAmount(Number(e.target.value))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="trailingPaymentDays">Días de Pago</Label>
                <Input
                  id="trailingPaymentDays"
                  type="number"
                  value={trailingPaymentDays}
                  onChange={(e) =>
                    setTrailingPaymentDays(Number(e.target.value))
                  }
                />
              </div>
            </TabsContent>

            <TabsContent value="salesCondition" className="mt-4 space-y-4">
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                <div className="mb-2 flex items-center">
                  <CreditCard className="mr-2 h-4 w-4 text-blue-600" />
                  <h4 className="font-medium text-blue-700">
                    Condición de Venta
                  </h4>
                </div>
                <p className="mb-3 text-sm text-blue-700">
                  Al asignar una condición de venta, estará definiendo los
                  términos comerciales que aplican para este cliente.
                </p>
                <SalesConditionSelector
                  value={salesConditionId}
                  onChange={setSalesConditionId}
                  label=""
                  placeholder="Seleccionar condición de venta"
                />
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button onClick={onSave}>Guardar Evaluación</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientRiskEditorModal;
